import { useState, useMemo, useCallback } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { ChevronLeft, Layers } from 'lucide-react';
import {
  AreaChart,
  Area,
  XAxis,
  YAxis,
  Tooltip,
  ResponsiveContainer,
} from 'recharts';
import { useLot } from '../hooks/useLots.js';
import { formatCurrency, formatDate } from '../lib/format.js';
import {
  STATUS_DIMENSIONS,
  CONDITION_DIMENSIONS,
  SCAN_DIMENSIONS,
  lotAgeMonths,
} from '../lib/lotsHelpers.js';
import EcoulementBadge from '../components/EcoulementBadge.jsx';
import LotAcquisitionPanel from '../components/LotAcquisitionPanel.jsx';
import LotFinancingPanel from '../components/LotFinancingPanel.jsx';

// Clés des valeurs « problématiques » qu'on met en évidence en rouge si > seuil
const CRITICAL_THRESHOLDS = {
  status_stale:    0.30,
  cond_bad:        0.10,
  scan_not_found:  0.20,
};

// ── Sparkline 12 mois ─────────────────────────────────────────────────────────

function buildSparkData(salesByMonth) {
  const months = [];
  const now = new Date();
  for (let i = 11; i >= 0; i--) {
    const d = new Date(now.getFullYear(), now.getMonth() - i, 1);
    const key = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
    const label = new Intl.DateTimeFormat('fr-FR', { month: 'short' }).format(d);
    months.push({ key, label, revenue_cents: 0, units: 0 });
  }
  if (Array.isArray(salesByMonth)) {
    for (const entry of salesByMonth) {
      const found = months.find((m) => m.key === entry.month);
      if (found) {
        found.revenue_cents = entry.revenue_cents ?? 0;
        found.units = entry.units ?? 0;
      }
    }
  }
  return months;
}

function SparkTooltip({ active, payload, label }) {
  if (!active || !payload?.length) return null;
  return (
    <div className="bg-white rounded-lg shadow border border-slate-200 px-3 py-2 text-xs min-w-[140px]">
      <p className="font-semibold text-slate-700 mb-1">{label}</p>
      <p className="text-slate-600">
        CA : <span className="font-semibold text-slate-900">{formatCurrency(payload[0]?.value * 100)}</span>
      </p>
      {payload[0]?.payload?.units > 0 && (
        <p className="text-slate-400 mt-0.5">{payload[0].payload.units} pièce{payload[0].payload.units > 1 ? 's' : ''}</p>
      )}
    </div>
  );
}

function LotSparkline({ salesByMonth }) {
  const data = buildSparkData(salesByMonth);
  const hasData = data.some((d) => d.revenue_cents > 0);

  if (!hasData) {
    return (
      <p className="text-xs text-slate-400 py-8 text-center">
        Aucune vente sur les 12 derniers mois.
      </p>
    );
  }

  const chartData = data.map((d) => ({ label: d.label, ca: d.revenue_cents / 100, units: d.units }));

  return (
    <ResponsiveContainer width="100%" height={220}>
      <AreaChart data={chartData} margin={{ top: 4, right: 4, bottom: 0, left: 0 }}>
        <defs>
          <linearGradient id="sparkGrad" x1="0" y1="0" x2="0" y2="1">
            <stop offset="5%"  stopColor="#3b82f6" stopOpacity={0.2} />
            <stop offset="95%" stopColor="#3b82f6" stopOpacity={0} />
          </linearGradient>
        </defs>
        <XAxis
          dataKey="label"
          tick={{ fontSize: 11, fill: '#94a3b8' }}
          tickLine={false}
          axisLine={false}
          interval={0}
        />
        <YAxis hide />
        <Tooltip content={<SparkTooltip />} />
        <Area
          type="monotone"
          dataKey="ca"
          stroke="#3b82f6"
          strokeWidth={1.5}
          fill="url(#sparkGrad)"
          dot={false}
          activeDot={{ r: 3, strokeWidth: 0, fill: '#3b82f6' }}
        />
      </AreaChart>
    </ResponsiveContainer>
  );
}

// ── Bloc dimension (tableau chiffré Label / Count / %) ───────────────────────

function DimensionBlock({ title, dimensions, lot, productCount }) {
  return (
    <div>
      <p className="text-xs font-semibold text-slate-500 uppercase tracking-wide mb-2">{title}</p>
      <table className="w-full text-sm">
        <tbody className="divide-y divide-slate-100">
          {dimensions.map((d) => {
            const count = lot[d.key] ?? 0;
            const pct = productCount > 0 ? Math.round((count / productCount) * 100) : 0;
            const threshold = CRITICAL_THRESHOLDS[d.key];
            const isCritical = threshold != null && productCount > 0 && count / productCount >= threshold;
            return (
              <tr key={d.key}>
                <td className="py-1.5 text-slate-700">{d.label}</td>
                <td className={`py-1.5 text-right tabular-nums whitespace-nowrap ${isCritical ? 'text-red-600 font-semibold' : 'text-slate-900 font-medium'}`}>
                  {count}
                </td>
                <td className="py-1.5 text-right text-xs text-slate-400 tabular-nums pl-2 w-12">
                  {pct} %
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}

// ── Badges statut produit ─────────────────────────────────────────────────────

const PRODUCT_STATUS_COLORS = {
  Live:    'bg-emerald-100 text-emerald-700',
  Sold:    'bg-emerald-50 text-emerald-600',
  Pending: 'bg-slate-100 text-slate-500',
  Stale:   'bg-slate-100 text-slate-400',
  OKREF:   'bg-slate-100 text-slate-500',
  Waiting_for_Price: 'bg-amber-100 text-amber-700',
};

function ProductStatusBadge({ status }) {
  const cls = PRODUCT_STATUS_COLORS[status] ?? 'bg-slate-100 text-slate-500';
  return (
    <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${cls}`}>
      {status ?? '—'}
    </span>
  );
}

// ── Tableau produits ──────────────────────────────────────────────────────────

const ALL_STATUSES = ['Live', 'Sold', 'Pending', 'Stale', 'OKREF', 'Waiting_for_Price'];
const ALL_CONDITIONS = ['Good', 'Normal', 'Bad', 'New'];

function LotProductsTable({ products }) {
  const navigate = useNavigate();
  const [statusFilter, setStatusFilter] = useState('');
  const [condFilter, setCondFilter] = useState('');
  const [skuSearch, setSkuSearch] = useState('');
  const [skuQuery, setSkuQuery] = useState('');

  // Debounce SKU search 300ms
  const handleSkuChange = useCallback((e) => {
    const val = e.target.value;
    setSkuSearch(val);
    clearTimeout(handleSkuChange._timer);
    handleSkuChange._timer = setTimeout(() => setSkuQuery(val), 300);
  }, []);

  const filtered = useMemo(() => {
    const q = skuQuery.trim().toLowerCase();
    return products.filter((p) => {
      if (statusFilter && p.status !== statusFilter) return false;
      if (condFilter && p.item_condition !== condFilter) return false;
      if (q) {
        const haystack = [p.sku, p.name, p.reference].filter(Boolean).join(' ').toLowerCase();
        if (!haystack.includes(q)) return false;
      }
      return true;
    });
  }, [products, statusFilter, condFilter, skuQuery]);

  return (
    <div className="space-y-3">
      {/* Search bar SKU */}
      <input
        type="search"
        value={skuSearch}
        onChange={handleSkuChange}
        placeholder="Rechercher par SKU, nom, référence…"
        className="w-full max-w-sm pl-3 pr-3 py-1.5 text-sm border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-400"
        aria-label="Rechercher un produit"
      />

      <div className="flex items-center justify-between gap-3 flex-wrap">
        <p className="text-sm font-semibold text-slate-800">
          {filtered.length.toLocaleString('fr-FR')} produit{filtered.length > 1 ? 's' : ''}
          {(statusFilter || condFilter || skuQuery) && (
            <span className="ml-1 text-slate-400 font-normal text-xs">(filtré)</span>
          )}
        </p>
        <div className="flex gap-2">
          <select
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
            className="text-xs border border-slate-300 rounded-lg px-2 py-1.5 focus:outline-none focus:ring-2 focus:ring-blue-400 bg-white"
            aria-label="Filtre statut"
          >
            <option value="">Tous les statuts</option>
            {ALL_STATUSES.map((s) => <option key={s} value={s}>{s}</option>)}
          </select>
          <select
            value={condFilter}
            onChange={(e) => setCondFilter(e.target.value)}
            className="text-xs border border-slate-300 rounded-lg px-2 py-1.5 focus:outline-none focus:ring-2 focus:ring-blue-400 bg-white"
            aria-label="Filtre condition"
          >
            <option value="">Toutes les conditions</option>
            {ALL_CONDITIONS.map((c) => <option key={c} value={c}>{c}</option>)}
          </select>
        </div>
      </div>

      {/* Mobile cards */}
      <div className="space-y-2 md:hidden">
        {filtered.map((p) => (
          <button
            key={p.sku}
            type="button"
            onClick={() => navigate(`/ventes/produits/${encodeURIComponent(p.sku)}`)}
            className="w-full text-left bg-white rounded-xl border border-slate-200 shadow-sm px-4 py-3 hover:bg-slate-50 active:bg-slate-100 transition-colors"
          >
            <div className="flex items-start justify-between gap-2">
              <div className="min-w-0">
                <p className="text-xs font-mono text-slate-500 truncate">{p.sku}</p>
                <p className="text-sm font-medium text-slate-900 truncate mt-0.5">{p.name ?? '—'}</p>
              </div>
              <div className="flex flex-col items-end gap-1 shrink-0">
                <ProductStatusBadge status={p.status} />
                {p.price_cents != null && (
                  <span className="text-xs font-semibold text-slate-700">{formatCurrency(p.price_cents)}</span>
                )}
              </div>
            </div>
          </button>
        ))}
      </div>

      {/* Desktop table */}
      <div className="hidden md:block bg-white rounded-xl border border-slate-200 shadow-sm overflow-x-auto">
        <table className="w-full text-sm">
          <thead>
            <tr className="border-b border-slate-100 bg-slate-50/50">
              <th className="text-left px-4 py-3 text-xs font-semibold text-slate-500 uppercase tracking-wide">SKU</th>
              <th className="text-left px-4 py-3 text-xs font-semibold text-slate-500 uppercase tracking-wide">Nom</th>
              <th className="text-left px-4 py-3 text-xs font-semibold text-slate-500 uppercase tracking-wide">Statut</th>
              <th className="text-left px-4 py-3 text-xs font-semibold text-slate-500 uppercase tracking-wide">Condition</th>
              <th className="text-right px-4 py-3 text-xs font-semibold text-slate-500 uppercase tracking-wide">Prix</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-100">
            {filtered.map((p) => (
              <tr
                key={p.sku}
                onClick={() => navigate(`/ventes/produits/${encodeURIComponent(p.sku)}`)}
                className="hover:bg-slate-50 cursor-pointer transition-colors"
              >
                <td className="px-4 py-2.5 font-mono text-xs text-slate-600 whitespace-nowrap">{p.sku}</td>
                <td className="px-4 py-2.5 text-slate-800 max-w-[220px] truncate">{p.name ?? '—'}</td>
                <td className="px-4 py-2.5"><ProductStatusBadge status={p.status} /></td>
                <td className="px-4 py-2.5 text-slate-500 text-xs">{p.item_condition ?? '—'}</td>
                <td className="px-4 py-2.5 text-right tabular-nums font-semibold text-slate-900">
                  {p.price_cents != null ? formatCurrency(p.price_cents) : '—'}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {filtered.length === 0 && (
        <p className="text-sm text-slate-400 text-center py-8">Aucun produit pour ces filtres.</p>
      )}
    </div>
  );
}

// ── Page ──────────────────────────────────────────────────────────────────────

export default function LotDetailPage() {
  const { lot_number } = useParams();
  const navigate = useNavigate();

  const { data, isLoading, error } = useLot(lot_number);

  if (isLoading) {
    return (
      <div className="space-y-4">
        <div className="h-8 w-32 bg-slate-100 animate-pulse rounded" />
        <div className="h-24 bg-slate-100 animate-pulse rounded-xl" />
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
          {Array.from({ length: 4 }).map((_, i) => (
            <div key={i} className="h-20 bg-slate-100 animate-pulse rounded-xl" />
          ))}
        </div>
        <div className="h-40 bg-slate-100 animate-pulse rounded-xl" />
      </div>
    );
  }

  if (error?.message?.includes('404') || (error && !data)) {
    return (
      <div className="space-y-4">
        <button
          type="button"
          onClick={() => navigate('/ventes/lots')}
          className="flex items-center gap-1 text-sm text-slate-500 hover:text-slate-800 transition-colors"
          aria-label="Retour à la liste des lots"
        >
          <ChevronLeft size={16} aria-hidden /> Retour aux lots
        </button>
        <div className="flex flex-col items-center justify-center gap-3 py-16 text-center">
          <Layers size={32} className="text-slate-300" />
          <p className="text-base font-semibold text-slate-700">Lot introuvable</p>
          <p className="text-sm text-slate-400">Le lot LT{lot_number} n&apos;existe pas dans l&apos;inventaire.</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="space-y-4">
        <button
          type="button"
          onClick={() => navigate('/ventes/lots')}
          className="flex items-center gap-1 text-sm text-slate-500 hover:text-slate-800 transition-colors"
          aria-label="Retour à la liste des lots"
        >
          <ChevronLeft size={16} aria-hidden /> Retour aux lots
        </button>
        <div className="rounded-lg bg-red-50 border border-red-200 px-4 py-3 text-sm text-red-700">
          Impossible de charger ce lot : {error.message}
        </div>
      </div>
    );
  }

  if (!data) return null;

  const summary = data.summary ?? data;
  const products = data.products ?? [];
  const salesByMonth = data.sales_by_month ?? [];

  const age = lotAgeMonths(summary.lot_date_min);

  const kpis = [
    { label: 'Nb pièces', value: (summary.product_count ?? 0).toLocaleString('fr-FR') },
    {
      label: 'Écoulement',
      value: <EcoulementBadge sold={summary.status_sold} total={summary.product_count} />,
    },
    { label: 'Valeur catalogue', value: formatCurrency(summary.catalog_value_cents) },
    { label: 'CA réel', value: formatCurrency(summary.ca_real_cents) },
  ];

  const productCount = summary.product_count ?? 0;

  return (
    <div className="space-y-5">

      {/* Retour */}
      <button
        type="button"
        onClick={() => navigate('/ventes/lots')}
        className="flex items-center gap-1 text-sm text-slate-500 hover:text-slate-800 transition-colors"
        aria-label="Retour à la liste des lots"
      >
        <ChevronLeft size={16} aria-hidden /> Retour aux lots
      </button>

      {/* Header 2 colonnes */}
      <div className="flex flex-col md:flex-row md:items-start md:gap-6">
        {/* Gauche : titre + sous-titre */}
        <div className="mb-4 md:mb-0 md:min-w-[120px]">
          <h2 className="text-xl font-bold text-slate-900">LT{summary.lot_number}</h2>
          <div className="flex flex-wrap items-center gap-2 mt-1 text-xs text-slate-500">
            {summary.lot_date_min && (
              <span>{formatDate(summary.lot_date_min)}</span>
            )}
            {age !== null && (
              <span className="text-slate-400">· {age} mois</span>
            )}
          </div>
        </div>

        {/* Droite : KPI cards 2×2 */}
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 flex-1">
          {kpis.map(({ label, value }) => (
            <div key={label} className="bg-white rounded-xl border border-slate-200 shadow-sm px-4 py-3">
              <p className="text-xs font-medium text-slate-500 uppercase tracking-wide">{label}</p>
              <div className="mt-1 text-lg font-bold tabular-nums text-slate-900 leading-tight">
                {value}
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Acquisition (coût HT/TTC + cost recovery) */}
      <LotAcquisitionPanel
        lotNumber={Number(summary.lot_number)}
        acquisition={summary.acquisition}
      />

      {/* Financement — dettes adossées à ce lot */}
      <LotFinancingPanel
        financing={data.financing ?? []}
        financedCents={data.financed_cents ?? 0}
        acquisitionCostCents={summary.acquisition?.cost_ttc_cents ?? null}
      />

      {/* État du lot — 3 blocs chiffrés */}
      <div className="bg-white rounded-xl border border-slate-200 shadow-sm px-4 py-4">
        <h3 className="text-sm font-semibold text-slate-800 mb-1">État du lot</h3>
        <p className="text-xs text-slate-400 mb-4">
          Sur {productCount.toLocaleString('fr-FR')} pièce{productCount > 1 ? 's' : ''}. Les chiffres en rouge signalent un problème.
        </p>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-x-8 gap-y-6">
          <DimensionBlock title="Status"    dimensions={STATUS_DIMENSIONS}    lot={summary} productCount={productCount} />
          <DimensionBlock title="Condition" dimensions={CONDITION_DIMENSIONS} lot={summary} productCount={productCount} />
          <DimensionBlock title="Scan"      dimensions={SCAN_DIMENSIONS}      lot={summary} productCount={productCount} />
        </div>
      </div>

      {/* Sparkline */}
      <div className="bg-white rounded-xl border border-slate-200 shadow-sm px-4 py-4">
        <h3 className="text-sm font-semibold text-slate-800 mb-3">Ventes — 12 derniers mois</h3>
        <LotSparkline salesByMonth={salesByMonth} />
      </div>

      {/* Distribution prix */}
      {(summary.price_min_cents != null || summary.price_max_cents != null) && (
        <div className="bg-white rounded-xl border border-slate-200 shadow-sm px-4 py-4">
          <h3 className="text-sm font-semibold text-slate-800 mb-3">Distribution des prix</h3>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            {[
              { label: 'Prix min',    val: summary.price_min_cents },
              { label: 'Prix moyen',  val: summary.price_avg_cents },
              { label: 'Prix médian', val: summary.price_median_cents },
              { label: 'Prix max',    val: summary.price_max_cents },
            ].map(({ label, val }) => (
              <div key={label} className="bg-slate-50 rounded-lg px-3 py-3">
                <p className="text-[11px] text-slate-400 uppercase tracking-wide">{label}</p>
                <p className="text-lg font-bold tabular-nums text-slate-900 mt-1">{formatCurrency(val)}</p>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Produits du lot */}
      <div className="space-y-3">
        <h3 className="text-sm font-semibold text-slate-800">Produits du lot</h3>
        {products.length > 0 ? (
          <LotProductsTable products={products} />
        ) : (
          <p className="text-sm text-slate-400">Aucun produit chargé pour ce lot.</p>
        )}
      </div>
    </div>
  );
}
